import { createContext, useState, useEffect, useRef, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authAPI, ConsultantProfile } from "@/lib/api/auth";
import { tokenManager } from "@/lib/api/client";
import type { CompanyRole } from "@/lib/companyRole";
import { isSkipAuthEnabled } from "@/lib/dev-users";
import type { FeatureFlags } from "@/lib/features";
import { getTokenExpiry } from "@/lib/jwt";
import { logger } from "@/lib/logger";
import { publishSessionLogout, subscribeToSessionLogout } from "@/lib/session-sync";

// Updated User interface to match backend API
export interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  created_at: string;
  full_name?: string | null;
  consultant_profile?: ConsultantProfile | null;
  /** The consultant's role in their own company (BE #1069), from `/users/me`
   *  and the login payload: an employee sees no Financials. Null for admins;
   *  missing on older backends, which reads as manager. */
  company_role?: CompanyRole | null;
  /** A published policy still needs acknowledging (BE #864); never set for admins. */
  has_pending_policies?: boolean;
  /** Environment feature flags (BE #898); missing means everything optional is off. */
  features?: FeatureFlags | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  /** Log in as a dev user using real backend auth (skip-auth mode only). */
  devLogin: (user: User) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
}

interface LoginCredentials {
  username: string; // Changed from email to username to match backend
  password: string;
  remember?: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export types for reuse
export type { AuthContextType, LoginCredentials };

interface AuthProviderProps {
  children: ReactNode;
}

/** How many seconds before expiry to schedule the refresh (2 minutes). */
const REFRESH_BEFORE_EXPIRY_S = 120;

export function AuthProvider({ children }: AuthProviderProps) {
  // Server-state cache, cleared on every session teardown: query keys are
  // user-agnostic ("timesheets", "time-entries", …), so cached data left
  // behind after a logout would render for the NEXT user who signs in within
  // the stale window (#815).
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Incremented for local and remote logout so async restore/refresh work that
  // started beforehand cannot resurrect the signed-out session.
  const logoutGenerationRef = useRef(0);
  // Keep a stable ref to the latest scheduleTokenRefresh so the timer callback
  // always calls the current closure without stale-capture issues.
  const scheduleRefreshRef = useRef<((token: string) => void) | undefined>(undefined);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleTokenRefresh = (token: string) => {
    clearRefreshTimer();

    const exp = getTokenExpiry(token);
    if (exp === null) {
      return;
    }

    const nowS = Math.floor(Date.now() / 1000);
    const refreshInMs = (exp - nowS - REFRESH_BEFORE_EXPIRY_S) * 1000;
    if (refreshInMs <= 0) {
      return;
    }

    const scheduledGeneration = logoutGenerationRef.current;
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const data = await authAPI.refreshToken();
        if (logoutGenerationRef.current === scheduledGeneration) {
          scheduleRefreshRef.current?.(data.access_token);
        } else {
          tokenManager.removeToken();
        }
      } catch {
        if (logoutGenerationRef.current !== scheduledGeneration) {
          tokenManager.removeToken();
          return;
        }
        // Refresh failed — clear session
        clearRefreshTimer();
        await authAPI.logout();
        localStorage.removeItem("bondIT_remember_me");
        setUser(null);
        queryClient.clear();
      }
    }, refreshInMs);
  };

  // Keep the ref pointing at the latest version of the function.
  useEffect(() => {
    scheduleRefreshRef.current = scheduleTokenRefresh;
  });

  useEffect(
    () =>
      subscribeToSessionLogout(() => {
        logoutGenerationRef.current += 1;
        if (refreshTimerRef.current !== null) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
        tokenManager.removeToken();
        localStorage.removeItem("bondIT_remember_me");
        setUser(null);
        setIsLoading(false);
        queryClient.clear();
      }),
    [queryClient]
  );

  useEffect(() => {
    const checkAuth = async () => {
      const restoreGeneration = logoutGenerationRef.current;
      try {
        // The access token is memory-only (issue #635), so a fresh page load
        // never has one. Exchange the HttpOnly refresh cookie for a new
        // access token; `null` simply means the user isn't logged in.
        const token = await authAPI.restoreSession();
        if (logoutGenerationRef.current !== restoreGeneration) {
          tokenManager.removeToken();
          return;
        }
        if (token) {
          const userData = await authAPI.refreshUserData();
          if (logoutGenerationRef.current !== restoreGeneration) {
            tokenManager.removeToken();
          } else if (userData) {
            setUser(userData);
            // Schedule proactive refresh using the restored access token
            scheduleRefreshRef.current?.(token);
          } else {
            // Session restored but the user fetch failed — clear the local
            // token WITHOUT hitting /auth/logout: revoking the refresh
            // cookie over a possibly-transient /users/me failure would
            // destroy a valid session (and every other tab's with it).
            tokenManager.removeToken();
          }
        }
      } catch (error) {
        logger.error("auth_restore_failed", { err: String(error) });
        tokenManager.removeToken();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);

      const loginResponse = await authAPI.login({
        username: credentials.username,
        password: credentials.password,
      });

      setUser(loginResponse.user);
      scheduleRefreshRef.current?.(loginResponse.access_token);
    } catch (error) {
      logger.error("login_failed", { err: String(error) });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Log in as a dev user via real backend auth (gets a real JWT token).
   *
   * Gated behind two checks so the hardcoded password literal is tree-shaken
   * out of production bundles and accidental invocations fail loudly:
   *   1. `import.meta.env.DEV` — statically replaced by Vite at build time;
   *      the entire dev-only body is eliminated from prod bundles.
   *   2. `isSkipAuthEnabled()` — runtime guard so a misset `VITE_SKIP_AUTH`
   *      in a non-dev build still throws synchronously.
   */
  const devLogin = async (devUser: User) => {
    if (!import.meta.env.DEV) {
      throw new Error("devLogin is not available in production builds");
    }
    if (!isSkipAuthEnabled()) {
      throw new Error("devLogin called outside skip-auth mode");
    }
    await login({ username: devUser.username, password: "secret" });
  };

  const logout = async () => {
    logoutGenerationRef.current += 1;
    clearRefreshTimer();
    localStorage.removeItem("bondIT_remember_me");
    setUser(null);
    setIsLoading(false);
    queryClient.clear();
    publishSessionLogout();
    await authAPI.logout();
  };

  const refreshUser = async (): Promise<User | null> => {
    const refreshGeneration = logoutGenerationRef.current;
    try {
      const userData = await authAPI.refreshUserData();
      if (logoutGenerationRef.current !== refreshGeneration) {
        tokenManager.removeToken();
        return null;
      }
      if (userData) {
        setUser(userData);
        return userData;
      }
      return null;
    } catch (error) {
      logger.error("refresh_user_failed", { err: String(error) });
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    devLogin,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
